import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Compass, Globe, User, Plus, LogOut, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { VentanaLogo } from '@/components/VentanaLogo';

export const Navbar = () => {
  const { user, isAuthenticated, login, logout, loading } = useAuth();
  const location = useLocation();

  const navLinks = [
    { to: '/explore', label: 'Explore', icon: Compass },
    { to: '/globe', label: 'Globe', icon: Globe },
  ];

  const authNavLinks = [
    { to: '/feed', label: 'Feed', icon: Plane },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? '/feed' : '/'} className="flex items-center" data-testid="logo-link">
            <VentanaLogo size="default" />
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-6">
            {isAuthenticated && authNavLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link flex items-center gap-2 text-sm font-medium ${isActive(link.to) ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link flex items-center gap-2 text-sm font-medium ${isActive(link.to) ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {loading ? (
              <div className="w-8 h-8 rounded-full bg-white/5 animate-pulse" />
            ) : isAuthenticated ? (
              <>
                <Link to="/upload" data-testid="upload-btn">
                  <Button className="btn-primary-gradient rounded-full px-4 py-2 text-sm font-medium">
                    <Plus className="w-4 h-4 mr-1" />
                    Upload
                  </Button>
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                    <button className="avatar-ring cursor-pointer focus:outline-none">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-purple-900 text-white">
                          {user?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#0F0F16] border-white/10">
                    <DropdownMenuItem asChild>
                      <Link to={`/profile/${user?.user_id}`} className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      onClick={logout} 
                      className="flex items-center gap-2 text-red-400 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button 
                onClick={login} 
                className="btn-primary-gradient rounded-full px-6 py-2 text-sm font-medium"
                data-testid="login-btn"
              >
                Sign In with Google
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-t border-white/5">
        <div className="flex justify-around py-2">
          {isAuthenticated && authNavLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${isActive(link.to) ? 'text-white' : 'text-gray-500'}`}
            >
              <link.icon className="w-5 h-5" />
              <span className="text-xs">{link.label}</span>
            </Link>
          ))}
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${isActive(link.to) ? 'text-white' : 'text-gray-500'}`}
            >
              <link.icon className="w-5 h-5" />
              <span className="text-xs">{link.label}</span>
            </Link>
          ))}
          {isAuthenticated && (
            <Link
              to={`/profile/${user?.user_id}`}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${location.pathname.startsWith('/profile') ? 'text-white' : 'text-gray-500'}`}
            >
              <User className="w-5 h-5" />
              <span className="text-xs">Profile</span>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};
